import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { TranslateService } from '@ngx-translate/core';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { environment } from '../../../../../environments/environment';
import { ReportNotificationComponent } from '../../../../@shared/components/report-notification/report-notification.component';
import { CookieAuthService } from '../../../../@shared/storage-variables/cookie-auth.service';

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  private readonly apiUrl = environment.api_url;
  private http = inject(HttpClient);
  private cookieAuthService = inject(CookieAuthService);
  private translateService = inject(TranslateService);
  private dialog = inject(MatDialog);

  private showNotificationDialog() {
    return this.dialog.open(ReportNotificationComponent, {
      position: { bottom: '20px', right: '20px' },
      width: '350px',
      height: '77px',
      hasBackdrop: false,
      disableClose: true,
      panelClass: 'notification-dialog',
    });
  }

  downloadReport(
    reportId: string,
    startDate: string,
    endDate: string,
    options?: { lang?: string; showNotification?: boolean },
  ): Observable<{ blob: Blob; filename: string }> {
    const companyObject = this.cookieAuthService.getCompanyObject;
    const companyId = companyObject?.Id_company?.toString();
    const lang =
      options?.lang || this.translateService.getCurrentLang() || 'es';
    const showNotification = options?.showNotification ?? false;

    let notificationRef = null;
    if (showNotification) {
      notificationRef = this.showNotificationDialog();
    }

    const requestBody = {
      startDate,
      endDate,
      companyId,
      lang,
    };

    return this.http
      .post(`${this.apiUrl}/reports/${reportId}/`, requestBody, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const blob = response.body!;
          const contentDisposition = response.headers.get(
            'Content-Disposition',
          );
          let filename = `report_${startDate}_${endDate}.xlsx`; // fallback

          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch) {
              filename = filenameMatch[1];
            }
          }

          if (notificationRef) {
            if (!blob || blob.size === 0) {
              notificationRef.componentInstance.setError(
                'There are not results for the date selected, please try selecting other',
              );
            } else {
              notificationRef.componentInstance.setComplete();
            }
          }

          return { blob, filename };
        }),
        catchError((error) => {
          if (notificationRef) {
            const errorMessage =
              error?.error?.message ||
              'Error generating file, please try later';
            notificationRef.componentInstance.setError(errorMessage);
          }
          return throwError(() => error);
        }),
      );
  }

  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
